import { useRef, useCallback } from "react";
import useOrderCheckoutApi from "@apis/useOrderCheckoutApi";
import useValidationErrors from "@util/hook/useValidationErrors";
import { isEmpty } from "@util/utils";
import { useHistory } from "react-router-dom";
import { ACCOUNT_BOOK_ORDERS_OVERVIEW_URL } from "@icoach/router/routerPath";
import { useSnackbar } from "notistack";

/**
 * 結帳提交 Hook
 * 負責處理結帳的提交邏輯
 */
const useOrderCheckoutSubmit = ({ onBeforeNavigate } = {}) => {
	const history = useHistory();
	const { enqueueSnackbar } = useSnackbar();
	const { postOrderCheckoutByPeopleApi } = useOrderCheckoutApi();
	const { validationErrors, updateValidationErrors, clearValidationErrors } = useValidationErrors();
	const apiSendingRef = useRef(false);

	/**
	 * 提交結帳
	 * @param {Object} params - 提交參數
	 * @returns {Promise<boolean>} 是否成功
	 */
	const submit = useCallback(
		async params => {
			if (apiSendingRef.current) return false;

			clearValidationErrors();
			apiSendingRef.current = true;

			try {
				const resp = await postOrderCheckoutByPeopleApi({
					receiverType: params?.receiverType ?? 0,
					receiverID: params?.receiverID ?? 0,
					clubID: params?.clubID ?? 0,
					receiverName: params?.receiverName ?? "",
					receiverPhone: params?.receiverPhone ?? "",
					employeeID: params?.employeeID ?? 0,
					orderDate: params?.orderDate,
					checkoutOrderIDs: params?.checkoutOrderIDs ?? [],
					items: params?.items ?? [],
					products: params?.products ?? [],
					deviceOrderItems: params?.deviceOrderItems ?? [],
					purchaseTimeType: params?.purchaseTimeType ?? 0,
					purchaseToolType: params?.purchaseToolType ?? 0,
					cashPayment: params?.cashPayment ?? 0,
					cardPayment: params?.cardPayment ?? 0,
					walletPayment: params?.walletPayment ?? 0,
					invoiceSetting: {
						issuingType: params?.invoiceSetting?.issuingType ?? 0,
						invoiceCarrierType: params?.invoiceSetting?.invoiceCarrierType ?? 0,
						invoiceCarrierNo: params?.invoiceSetting?.invoiceCarrierNo ?? "",
						taxTitle: params?.invoiceSetting?.taxTitle ?? "",
						taxID: params?.invoiceSetting?.taxID ?? "",
						donationCode: params?.invoiceSetting?.donationCode ?? "",
						invoiceNo: params?.invoiceSetting?.invoiceNo ?? "",
						issuingEmail: params?.invoiceSetting?.issuingEmail ?? "",
					},
					campaignID: params?.campaignID ?? 0,
					voucherCode: params?.voucherCode ?? "",
					discountTotalPrice: params?.discountTotalPrice ?? 0,
				});
				const { isError = false, result } = resp || {};

				if (isError) {
					// 0002 錯誤：驗證錯誤
					updateValidationErrors(result);
					apiSendingRef.current = false;
					return false;
				} else if (isEmpty(resp)) {
					// 失敗：無回應
					apiSendingRef.current = false;
					return false;
				} else {
					enqueueSnackbar("結帳完成", { variant: "success" });
					if (typeof onBeforeNavigate === 'function') onBeforeNavigate();
					// 導航到訂單總覽頁面
					history.push(ACCOUNT_BOOK_ORDERS_OVERVIEW_URL);
					apiSendingRef.current = false;
					return true;
				}
			} catch (error) {
				console.error("提交結帳失敗:", error);
				apiSendingRef.current = false;
				return false;
			}
		},
		// eslint-disable-next-line
		[postOrderCheckoutByPeopleApi, updateValidationErrors, clearValidationErrors],
	);

	/**
	 * 檢查是否正在提交中
	 */
	const isSubmitting = useCallback(
		() => {
			return apiSendingRef.current;
		},
		// eslint-disable-next-line
		[],
	);

	return {
		submit,
		isSubmitting,
		validationErrors,
		clearValidationErrors,
	};
};

export default useOrderCheckoutSubmit;
